<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="status"/>
        <div class="pi-status__head">
          <p class="pi-page__lead">
            Saúde da coleta e do processamento, perdas por dia e as definições em vigor. Os números aqui são técnicos:
            perder evento é aceito e contado, nunca escondido. Passe o mouse sobre uma situação para ver o que ela significa.
          </p>
          <pi-export-screen screen="Status" :filters="screenExportFilters"/>
        </div>

        <div v-for="alert in alerts" :key="alert.key" class="pi-status__alert" :class="'pi-status__alert--' + alert.tone" role="status">
          <strong class="pi-status__alert-title">{{ alert.title }}</strong>
          <span>{{ alert.text }}</span>
          <span v-if="alert.detail" class="pi-status__alert-detail">{{ alert.detail }}</span>
        </div>

        <div class="pi-subtabs" role="tablist" aria-label="Seções do status">
          <button v-for="(tab, index) in sectionTabs" :id="'pi-status-tab-' + tab.key" :key="tab.key" ref="sectionButtons"
                  type="button" role="tab" class="pi-subtab" :class="{ 'is-on': section === tab.key }"
                  :aria-selected="section === tab.key ? 'true' : 'false'" :aria-controls="'pi-status-panel-' + tab.key"
                  :tabindex="section === tab.key ? 0 : -1"
                  @click="selectSection(tab.key)" @keydown="onSectionKey($event, index)">
            {{ tab.label }}<span v-if="tab.count" class="pi-subtab__count pi-badge" :class="'pi-badge--' + tab.tone"
                                 :title="tab.hint" aria-hidden="true">{{ tab.count }}</span><span v-if="tab.hint" class="pi-sr">: {{ tab.hint }}</span>
          </button>
        </div>

        <div v-show="section === 'processamento'" id="pi-status-panel-processamento" role="tabpanel" aria-labelledby="pi-status-tab-processamento">
          <pi-data-state :loading="status.loading" :error="status.error" :envelope="status.envelope" :catalog-link="false" @retry="loadStatus">
            <section class="pi-section" aria-labelledby="pi-status-jobs">
              <div class="pi-section__head">
                <h2 id="pi-status-jobs" class="pi-section__title">Processamento (jobs)</h2>
                <div class="pi-status__choice">
                  <div class="pi-toggle" role="group" aria-label="Quais jobs mostrar">
                    <button v-for="option in jobFilterOptions" :key="option.key" type="button" class="pi-toggle__btn"
                            :class="{ 'is-on': jobFilter === option.key }" :aria-pressed="jobFilter === option.key ? 'true' : 'false'"
                            @click="jobFilter = option.key">{{ option.label }} ({{ option.count }})</button>
                  </div>
                  <pi-definition-tooltip info-key="status.jobFilter" label="Quais jobs mostrar"/>
                </div>
                <pi-export-menu title="Processamento (jobs)" screen="Status" :columns="jobColumns" :rows="visibleWatermarks" :envelope="status.envelope"
                                :filters="jobExportFilters" :disabled="statusUnavailable || !visibleWatermarks.length"/>
              </div>
              <pi-legend :entries="jobLegend"/>
              <div v-if="visibleWatermarks.length" class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">Marcas d'água dos jobs</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in jobHeaderColumns" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in visibleWatermarks" :key="row.key">
                    <td class="pi-status__job">
                      <span v-if="row.title" class="pi-status__job-title">{{ row.title }}</span>
                      <span v-if="row.scope" class="pi-table__sub">{{ row.scope }}</span>
                      <code class="pi-status__job-key">{{ row.key }}</code>
                      <span v-if="row.lastValue" class="pi-table__sub">marca: {{ row.lastValue }}</span>
                    </td>
                    <td>{{ row.processedUntil }}</td>
                    <td>{{ row.lastRun }}</td>
                    <td>{{ row.lastSuccess }}</td>
                    <td>
                      <span class="pi-badge" :class="'pi-badge--' + row.status.tone" :title="row.status.text">{{ row.status.label }}</span>
                      <span v-if="row.partial" class="pi-badge pi-badge--warn" :title="partialFlag.text">{{ partialFlag.label }}</span>
                    </td>
                    <td class="is-num">{{ row.rows }}</td>
                    <td class="is-num">{{ row.duration }}</td>
                    <td class="pi-status__error">{{ row.lastError }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p v-else-if="watermarks.length" class="pi-status__none">Nenhum job precisa de atenção agora.</p>
              <p v-else class="pi-status__none">Nenhum job executado ainda.</p>
            </section>
          </pi-data-state>
        </div>

        <div v-show="section === 'coleta'" id="pi-status-panel-coleta" role="tabpanel" aria-labelledby="pi-status-tab-coleta">
          <pi-data-state :loading="status.loading" :error="status.error" :envelope="status.envelope" :catalog-link="false" @retry="loadStatus">
            <section class="pi-section" aria-labelledby="pi-status-sinks">
              <div class="pi-section__head">
                <h2 id="pi-status-sinks" class="pi-section__title">Coleta por instância (últimas 24 h)</h2>
                <p class="pi-section__hint">Heartbeat a cada 60 s; mais de {{ staleMinutes }} min sem heartbeat é sinal de instância parada ou sink desligado.</p>
                <pi-export-menu title="Coleta por instância (últimas 24 h)" screen="Status" :columns="sinkColumns" :rows="sinks" :envelope="status.envelope"
                                :disabled="statusUnavailable || !sinks.length"/>
              </div>
              <pi-legend :entries="sinkLegend"/>
              <div v-if="sinks.length" class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">Heartbeats dos sinks por instância</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in sinkHeaderColumns" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in sinks" :key="row.key">
                    <td>{{ row.source }}</td>
                    <td><code>{{ row.instance }}</code></td>
                    <td>
                      {{ row.heartbeat }}
                      <span class="pi-table__sub">{{ row.elapsed }}</span>
                      <span v-if="row.staleness" class="pi-badge" :class="'pi-badge--' + row.staleness.tone"
                            :title="row.staleness.text">{{ row.staleness.label }}</span>
                    </td>
                    <td class="is-num">{{ row.accepted }}</td>
                    <td class="is-num">
                      {{ row.dropped }}<span class="pi-table__sub">{{ row.droppedPct }}</span>
                      <span v-if="row.invalidText" class="pi-table__sub pi-status__invalid" :title="invalidLegend">{{ row.invalidText }}</span>
                    </td>
                    <td class="is-num"><span :class="{ 'pi-status__stripped': row.strippedCount > 0 }" :title="row.strippedTitle">{{ row.stripped }}</span></td>
                    <td><span class="pi-badge" :class="'pi-badge--' + row.breaker.tone" :title="row.breakerText">{{ row.breaker.label }}</span></td>
                    <td><span class="pi-badge" :class="row.configEnabled ? 'pi-badge--ok' : 'pi-badge--muted'"
                              :title="configText">{{ row.configEnabled ? 'Ligado' : 'Desligado' }}</span></td>
                    <td class="pi-status__error">{{ row.lastError }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="pi-status__none">Nenhum heartbeat recebido. Com o sink desligado, nenhuma instância grava heartbeat.</p>
            </section>
          </pi-data-state>
        </div>

        <div v-show="section === 'perdas'" id="pi-status-panel-perdas" role="tabpanel" aria-labelledby="pi-status-tab-perdas">
          <pi-data-state :loading="status.loading" :error="status.error" :envelope="status.envelope" :catalog-link="false" @retry="loadStatus">
            <section class="pi-section" aria-labelledby="pi-status-loss">
              <div class="pi-section__head">
                <h2 id="pi-status-loss" class="pi-section__title">Perdas por dia</h2>
                <p class="pi-section__hint">Eventos descartados na borda (fila cheia, disjuntor, inválidos, falha de gravação).</p>
                <pi-export-menu title="Perdas por dia" screen="Status" :columns="lossColumns" :rows="dailyLoss" :envelope="status.envelope"
                                :disabled="statusUnavailable || !dailyLoss.length"/>
              </div>
              <pi-legend :entries="lossLegend" title="Como ler"/>
              <div v-if="dailyLoss.length" class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">Eventos aceitos e descartados por dia e origem</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in lossHeaderColumns" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in dailyLoss" :key="row.key">
                    <td>{{ row.date }}</td>
                    <td>{{ row.source }}</td>
                    <td class="is-num">{{ row.accepted }}</td>
                    <td class="is-num">
                      {{ row.dropped }}
                      <span v-if="row.invalidText" class="pi-table__sub pi-status__invalid" :title="invalidLegend">{{ row.invalidText }}</span>
                    </td>
                    <td class="is-num">{{ row.droppedPct }}</td>
                    <td class="is-num"><span :class="{ 'pi-status__stripped': row.strippedCount > 0 }" :title="strippedLegend">{{ row.stripped }}</span></td>
                    <td><span :title="row.partial ? partialDayText : ''">{{ row.partial ? 'Não (parcial)' : 'Sim' }}</span></td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="pi-status__none">Sem contagem de perdas no período.</p>
            </section>
          </pi-data-state>
        </div>

        <div v-show="section === 'configuracao'" id="pi-status-panel-configuracao" role="tabpanel" aria-labelledby="pi-status-tab-configuracao">
          <pi-data-state :loading="status.loading" :error="status.error" :envelope="status.envelope" :catalog-link="false" @retry="loadStatus">
            <section class="pi-section" aria-labelledby="pi-status-config">
              <div class="pi-section__head">
                <h2 id="pi-status-config" class="pi-section__title">Chaves de configuração</h2>
                <span class="pi-badge" :class="uiEnabled ? 'pi-badge--ok' : 'pi-badge--muted'">
                  {{ uiEnabled ? 'Painel ligado' : 'Painel desligado' }}
                </span>
                <pi-export-menu title="Chaves de configuração" screen="Status" :columns="configColumns" :rows="config" :envelope="status.envelope"
                                :disabled="statusUnavailable || !config.length"/>
              </div>
              <pi-legend :entries="configLegend" title="Como ler"/>
              <div v-if="config.length" class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">Chaves de pa.sink_config</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in configHeaderColumns" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in config" :key="row.key">
                    <td><code>{{ row.key }}</code></td>
                    <td><span class="pi-badge" :class="row.enabled ? 'pi-badge--ok' : 'pi-badge--muted'">{{ row.enabled ? 'Ligada' : 'Desligada' }}</span></td>
                    <td>{{ row.shardsText }}</td>
                    <td>{{ dateTime(row.updatedAtUtc) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <p v-else class="pi-status__none">Nenhuma chave cadastrada.</p>
            </section>
          </pi-data-state>
        </div>

        <div v-show="section === 'categorias'" id="pi-status-panel-categorias" role="tabpanel" aria-labelledby="pi-status-tab-categorias">
          <section class="pi-section" aria-labelledby="pi-status-categories">
            <div class="pi-section__head">
              <h2 id="pi-status-categories" class="pi-section__title">Categorias e segmentações</h2>
              <label class="pi-status__search">
                <span class="pi-sr">Buscar categoria</span>
                <input v-model="categorySearch" type="search" class="form-control pi-input" maxlength="60"
                       placeholder="Buscar por nome, valor ou origem">
              </label>
              <pi-definition-tooltip info-key="status.categorySearch" label="Buscar categoria"/>
              <pi-export-menu title="Categorias e segmentações" screen="Status" :columns="categoryColumns" :rows="categoryTable"
                              :filters="categoryExportFilters" :disabled="!categoryTable.length"/>
            </div>
            <p class="pi-section__hint">
              Todos os cortes usados no painel: o que cada um classifica, de onde vem e onde aparece.
              Porte e perfil da empresa são coisas diferentes: porte é tamanho, perfil é a classificação manual do time.
            </p>
            <div v-for="group in categoryGroups" :key="group.key" class="pi-cat__group">
              <h3 class="pi-subtitle">{{ group.label }}</h3>
              <p class="pi-section__hint">{{ group.hint }}</p>
              <div class="pi-cat__grid">
                <article v-for="item in group.items" :id="'pi-cat-' + item.key" :key="item.key" class="pi-cat">
                  <h4 class="pi-cat__name">
                    {{ item.name }}
                    <span v-if="item.filter" class="pi-badge pi-badge--info">filtro</span>
                  </h4>
                  <p class="pi-cat__question">{{ item.question }}</p>
                  <p class="pi-cat__text"><strong>De onde vem:</strong> {{ item.source }}</p>
                  <ul v-if="item.values.length" class="pi-cat__values">
                    <li v-for="value in item.values" :key="value.label">
                      <span class="pi-cat__value">{{ value.label }}</span><span v-if="value.text"> — {{ value.text }}</span>
                    </li>
                  </ul>
                  <p class="pi-cat__text pi-cat__used"><strong>Onde aparece:</strong> {{ item.usedIn }}</p>
                </article>
              </div>
            </div>
            <p v-if="!categoryGroups.length" class="pi-status__none">Nenhuma categoria para a busca.</p>
          </section>
        </div>

        <div v-show="section === 'eventos'" id="pi-status-panel-eventos" role="tabpanel" aria-labelledby="pi-status-tab-eventos">
          <section class="pi-section" aria-labelledby="pi-status-events">
            <div class="pi-section__head">
              <h2 id="pi-status-events" class="pi-section__title">Catálogo de eventos</h2>
              <label class="pi-status__search">
                <span class="pi-sr">Buscar evento</span>
                <input v-model="eventSearch" type="search" class="form-control pi-input" maxlength="60"
                       placeholder="Buscar por nome, módulo, descrição ou onde é coletado">
              </label>
              <pi-definition-tooltip info-key="status.eventSearch" label="Buscar evento"/>
              <pi-export-menu title="Catálogo de eventos" screen="Status" :columns="eventColumns" :rows="visibleEvents" :envelope="events.envelope"
                              :filters="eventExportFilters" :disabled="!eventsLoaded || !!events.error || !visibleEvents.length"/>
            </div>
            <pi-data-state :loading="events.loading" :error="events.error" :envelope="events.envelope"
                           :empty="eventsLoaded && !eventRows.length" empty-message="Nenhum evento catalogado."
                           :show-footer="false" @retry="loadEvents">
              <paged-table v-if="visibleEvents.length" :rows="visibleEvents" storage-key="pi-catalog-events"
                           :default-page-size="25" v-slot="{ rows }">
                <thead>
                <tr>
                  <template v-for="column in eventHeaderColumns">
                    <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="eventSort" @sort="sortEvents"/>
                    <pi-th v-else :key="column.key" :column="column"/>
                  </template>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in rows" :key="row.name">
                  <td class="pi-status__event">
                    <code>{{ row.name }}</code><span class="pi-table__sub">{{ row.description }}</span>
                    <span v-if="row.collectedWhere" class="pi-table__sub pi-status__collect"><strong>Onde:</strong> {{ row.collectedWhere }}</span>
                    <span v-if="row.collectedHow" class="pi-table__sub pi-status__collect"><strong>Como:</strong> {{ row.collectedHow }}</span>
                  </td>
                  <td>{{ row.moduleName }}</td>
                  <td>{{ row.featureText }}</td>
                  <td>{{ row.ownerText }} · {{ row.sourceText }}</td>
                  <td>{{ row.stage }}</td>
                  <td>
                    <span class="pi-badge" :class="row.isEnabled ? 'pi-badge--ok' : 'pi-badge--muted'">{{ row.isEnabled ? 'Habilitado' : 'Desligado' }}</span>
                    <span v-if="row.status" class="pi-table__sub">{{ row.status }}</span>
                  </td>
                  <td>{{ row.countsText }}</td>
                  <td>{{ row.rolesText }}</td>
                </tr>
                </tbody>
              </paged-table>
              <p v-else class="pi-status__none">Nenhum evento para a busca.</p>
            </pi-data-state>
          </section>
        </div>

        <div v-show="section === 'metricas'" id="pi-status-panel-metricas" role="tabpanel" aria-labelledby="pi-status-tab-metricas">
          <section class="pi-section" aria-labelledby="pi-status-metrics">
            <div class="pi-section__head">
              <h2 id="pi-status-metrics" class="pi-section__title">Catálogo de métricas</h2>
              <p class="pi-section__hint">Cada número do painel cita a versão da definição que o calculou.</p>
              <pi-export-menu title="Catálogo de métricas" screen="Status" :columns="metricColumns" :rows="metricRows" :envelope="metrics.envelope"
                              :disabled="!metricsLoaded || !!metrics.error || !metricRows.length"/>
            </div>
            <pi-data-state :loading="metrics.loading" :error="metrics.error" :envelope="metrics.envelope"
                           :empty="metricsLoaded && !metricRows.length" empty-message="Nenhuma métrica catalogada."
                           :show-footer="false" @retry="loadMetrics">
              <div class="pi-table-wrap">
                <table class="pi-table">
                  <caption class="pi-sr">Definições versionadas das métricas</caption>
                  <thead>
                  <tr>
                    <pi-th v-for="column in metricHeaderColumns" :key="column.key" :column="column"/>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="row in metricRows" :key="row.key">
                    <td class="pi-status__metric">
                      {{ row.name }}<span class="pi-table__sub"><code>{{ row.metricKey }}</code></span>
                      <span v-if="row.description" class="pi-table__sub">{{ row.description }}</span>
                    </td>
                    <td class="is-num">{{ row.version }}</td>
                    <td>{{ row.formula }}</td>
                    <td>{{ row.numerator }}</td>
                    <td>{{ row.denominator }}</td>
                    <td>{{ row.window }}<span v-if="row.timezoneRule" class="pi-table__sub">{{ row.timezoneRule }}</span></td>
                    <td>{{ row.validity }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </pi-data-state>
          </section>
        </div>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import pagedTable from "../../components/default/pagedTable.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piLegend from "../../components/pi/piLegend.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piSortTh from "../../components/pi/piSortTh.vue";
import piTh from "../../components/pi/piTh.vue";
import { describeError, piGet } from "../../components/pi/piApi.js";
import { columnByKey, screenColumns } from "../../components/pi/piColumns.js";
import { exportProvide } from "../../components/pi/piExport.js";
import { formatDateTime, isNumber } from "../../components/pi/piFormat.js";
import { nextSort, optionLabel, searchRows, sortRows } from "../../components/pi/piQuery.js";
import { HEARTBEAT_STALE_MINUTES } from "../../components/pi/piState.js";
import { CATEGORY_COLUMNS, categoryCount, categoryTableRows, searchCategories } from "../../components/pi/piCategoriesModel.js";
import {
  CONFIG_COLUMNS, CONFIG_LEGEND, EVENT_COLUMNS, JOB_COLUMNS, JOB_FILTERS, JOB_STATUS_LEGEND, LOSS_COLUMNS, LOSS_LEGEND, METRIC_COLUMNS, PARTIAL_FLAG,
  SINK_COLUMNS, SINK_LEGEND, STATUS_SECTIONS, collectionAlerts, configRows, dailyLossRows, eventRows, filterJobs, metricRows, sinkRows, statusSection,
  watermarkRows,
} from "../../components/pi/piStatusModel.js";
import { PI_CATALOG_EVENTS, PI_CATALOG_METRICS, PI_STATUS } from "../../../../urlConfig";

const list = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const blankRequest = () => ({ loading: false, error: null, envelope: null, seq: 0 });

const legendEntry = (key) => SINK_LEGEND.filter((entry) => entry.key === key)[0];

const plural = (count, one, many) => count + " " + (count === 1 ? one : many);

const searchLine = (label, term) => {
  const text = typeof term === "string" ? term.trim() : "";
  return text ? label + ": \"" + text + "\"" : "";
};

const sortLine = (columns, state) => {
  const column = state ? columnByKey(columns, state.sort) : null;
  return column ? "Ordem: " + column.label + (state.desc ? ", decrescente" : ", crescente") : "";
};

export default {
  name: "pi-status",
  components: { boxDefault, pagedTable, piTabs, piDataState, piLegend, piDefinitionTooltip, piExportMenu, piExportScreen, piSortTh, piTh },
  provide: exportProvide,
  data() {
    return {
      status: blankRequest(),
      events: blankRequest(),
      metrics: blankRequest(),
      eventsLoaded: false,
      metricsLoaded: false,
      eventSearch: "",
      eventSort: { sort: "name", desc: false },
      staleMinutes: HEARTBEAT_STALE_MINUTES,
      jobFilter: "all",
      categorySearch: "",
      jobLegend: JOB_STATUS_LEGEND,
      sinkLegend: SINK_LEGEND,
      lossLegend: LOSS_LEGEND,
      configLegend: CONFIG_LEGEND,
      partialFlag: PARTIAL_FLAG,
      configText: CONFIG_LEGEND[0].text,
      partialDayText: LOSS_LEGEND[1].text,
      invalidLegend: legendEntry("invalid").text,
      strippedLegend: legendEntry("stripped").text,
    };
  },
  computed: {
    section() {
      return statusSection(this.$route && this.$route.query ? this.$route.query.secao : "");
    },
    sectionTabs() {
      const attention = this.watermarks.filter((row) => row.attention);
      const failed = attention.some((row) => row.status.tone === "bad");
      const sinkTrouble = this.sinks.filter((row) => row.trouble);
      const sinkBad = this.sinks.some((row) => row.trouble && (row.breaker.tone === "bad" || row.invalidCount > 0));
      const lossDays = list(this.statusData.dailyLoss).filter((row) => isNumber(row.dropped) && row.dropped > 0).length;
      const enabledEvents = this.eventRows.filter((row) => row.isEnabled).length;
      const counts = {
        processamento: attention.length
          ? { count: String(attention.length), tone: failed ? "bad" : "warn", hint: plural(attention.length, "job com atenção", "jobs com atenção") }
          : null,
        coleta: sinkTrouble.length
          ? { count: String(sinkTrouble.length), tone: sinkBad ? "bad" : "warn", hint: plural(sinkTrouble.length, "instância com problema", "instâncias com problema") }
          : null,
        perdas: lossDays ? { count: String(lossDays), tone: "warn", hint: plural(lossDays, "dia com descarte", "dias com descarte") } : null,
        eventos: this.eventsLoaded
          ? { count: enabledEvents + "/" + this.eventRows.length, tone: "muted", hint: enabledEvents + " de " + this.eventRows.length + " habilitados" }
          : null,
        categorias: { count: String(categoryCount()), tone: "muted", hint: plural(categoryCount(), "categoria", "categorias") },
        metricas: this.metricsLoaded ? { count: String(this.metricRows.length), tone: "muted", hint: plural(this.metricRows.length, "métrica", "métricas") } : null,
      };
      return STATUS_SECTIONS.map((tab) => Object.assign({ count: "", tone: "muted", hint: "" }, tab, counts[tab.key] || {}));
    },
    categoryGroups() {
      return searchCategories(this.categorySearch);
    },
    categoryTable() {
      return categoryTableRows(this.categoryGroups);
    },
    categoryColumns() {
      return CATEGORY_COLUMNS;
    },
    jobFilterOptions() {
      return JOB_FILTERS.map((option) => ({
        key: option.key,
        label: option.label,
        count: option.key === "attention" ? this.watermarks.filter((row) => row.attention).length : this.watermarks.length,
      }));
    },
    visibleWatermarks() {
      return filterJobs(this.watermarks, this.jobFilter);
    },
    statusData() {
      const data = this.status.envelope && this.status.envelope.data;
      return data && typeof data === "object" ? data : {};
    },
    statusUnavailable() {
      return !this.status.envelope || !!this.status.error;
    },
    uiEnabled() {
      return this.statusData.uiEnabled === true;
    },
    alerts() {
      return collectionAlerts(this.statusData.sinks);
    },
    config() {
      return configRows(this.statusData.config);
    },
    sinks() {
      return sinkRows(this.statusData.sinks);
    },
    watermarks() {
      return watermarkRows(this.statusData.watermarks);
    },
    dailyLoss() {
      return dailyLossRows(this.statusData.dailyLoss);
    },
    eventRows() {
      return eventRows(this.events.envelope && this.events.envelope.data);
    },
    visibleEvents() {
      const found = searchRows(this.eventRows, this.eventSearch, ["name", "moduleName", "description", "operationKey", "collectedWhere", "collectedHow"]);
      return sortRows(found, this.eventSort.sort, this.eventSort.desc);
    },
    metricRows() {
      return metricRows(this.metrics.envelope && this.metrics.envelope.data);
    },
    jobColumns() {
      return JOB_COLUMNS;
    },
    jobHeaderColumns() {
      return screenColumns(JOB_COLUMNS);
    },
    sinkColumns() {
      return SINK_COLUMNS;
    },
    sinkHeaderColumns() {
      return screenColumns(SINK_COLUMNS);
    },
    lossColumns() {
      return LOSS_COLUMNS;
    },
    lossHeaderColumns() {
      return screenColumns(LOSS_COLUMNS);
    },
    configColumns() {
      return CONFIG_COLUMNS;
    },
    configHeaderColumns() {
      return screenColumns(CONFIG_COLUMNS);
    },
    eventColumns() {
      return EVENT_COLUMNS;
    },
    eventHeaderColumns() {
      return screenColumns(EVENT_COLUMNS);
    },
    metricColumns() {
      return METRIC_COLUMNS;
    },
    metricHeaderColumns() {
      return screenColumns(METRIC_COLUMNS);
    },
    jobExportFilters() {
      return ["Jobs mostrados: " + optionLabel(JOB_FILTERS.map((option) => ({ value: option.key, label: option.label })), this.jobFilter)];
    },
    categoryExportFilters() {
      return [searchLine("Buscar categoria", this.categorySearch)];
    },
    eventExportFilters() {
      return [searchLine("Buscar evento", this.eventSearch), sortLine(EVENT_COLUMNS, this.eventSort)];
    },
    screenExportFilters() {
      return this.jobExportFilters.concat(this.categoryExportFilters, this.eventExportFilters);
    },
  },
  created() {
    this.loadStatus();
    this.loadEvents();
    this.loadMetrics();
  },
  mounted() {
    this.scrollToCategory();
  },
  watch: {
    "$route.hash"() {
      this.scrollToCategory();
    },
  },
  methods: {
    scrollToCategory() {
      const hash = this.$route && this.$route.hash;
      if (this.section !== "categorias" || !hash) return;
      this.$nextTick(() => {
        const target = document.getElementById(hash.slice(1));
        if (target && target.scrollIntoView) target.scrollIntoView({ block: "start" });
      });
    },
    selectSection(key) {
      if (key === this.section || !this.$router) return;
      const query = Object.assign({}, this.$route.query, { secao: key });
      this.$router.replace({ query: query }, () => {}, () => {});
    },
    onSectionKey(event, index) {
      const last = this.sectionTabs.length - 1;
      const moves = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last };
      if (!Object.prototype.hasOwnProperty.call(moves, event.key)) return;
      event.preventDefault();
      const target = moves[event.key];
      this.selectSection(this.sectionTabs[target].key);
      this.$nextTick(() => {
        const buttons = this.$refs.sectionButtons || [];
        if (buttons[target]) buttons[target].focus();
      });
    },
    run(slot, url, action, done) {
      const request = this[slot];
      request.seq += 1;
      const seq = request.seq;
      request.loading = true;
      request.error = null;
      piGet(url)
        .then((envelope) => {
          if (seq !== request.seq) return;
          request.envelope = envelope;
          if (done) done();
        })
        .catch((error) => {
          if (seq !== request.seq) return;
          request.envelope = null;
          request.error = describeError(error, action);
        })
        .then(() => {
          if (seq === request.seq) request.loading = false;
        });
    },
    loadStatus() {
      this.run("status", PI_STATUS, "carregar o status da coleta");
    },
    loadEvents() {
      this.run("events", PI_CATALOG_EVENTS, "carregar o catálogo de eventos", () => {
        this.eventsLoaded = true;
      });
    },
    loadMetrics() {
      this.run("metrics", PI_CATALOG_METRICS, "carregar o catálogo de métricas", () => {
        this.metricsLoaded = true;
      });
    },
    sortEvents(column) {
      this.eventSort = nextSort(this.eventSort, column.key, column.startDescending);
    },
    dateTime(value) {
      return formatDateTime(value);
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-status__head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px 16px;
}

.pi-status__head .pi-page__lead {
  flex: 1 1 420px;
}

.pi-status__choice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pi-status__event,
.pi-status__metric {
  min-width: 320px;
  max-width: 560px;
}

.pi-status__collect {
  margin-top: 2px;
}

.pi-subtabs {
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  gap: 6px;
  margin: 0 0 16px;
  padding: 4px;
  overflow-x: auto;
  border-radius: 8px;
  background: #f3f4f7;
}

.pi-subtab {
  display: inline-flex;
  flex: 0 0 auto;
  gap: 6px;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #4e5560;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.pi-subtab:hover {
  color: #1d12b9;
}

.pi-subtab:focus {
  outline: 2px solid #1b59f8;
  outline-offset: -2px;
}

.pi-subtab.is-on {
  background: #ffffff;
  color: #1d12b9;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.pi-subtab__count {
  padding: 0 6px;
  font-size: 10px;
  line-height: 16px;
}

.pi-status__job {
  min-width: 260px;
  max-width: 420px;
}

.pi-status__job-title {
  display: block;
  font-weight: 600;
  color: #1f2933;
}

.pi-page code.pi-status__job-key {
  display: block;
  margin-top: 2px;
  font-size: 11px;
  color: #6b7280;
  word-break: break-all;
}

.pi-status__none {
  margin: 0;
  font-size: 12px;
  color: #6b7280;
}

.pi-status__alert {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0 0 12px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 18px;
}

.pi-status__alert--warn {
  background: #fff4dc;
  color: #7a4f00;
}

.pi-status__alert--bad {
  background: #fdecee;
  color: #a3172b;
}

.pi-status__alert-title {
  font-weight: 600;
}

.pi-status__alert-detail {
  font-family: monospace;
  word-break: break-word;
}

.pi-status__invalid {
  color: #a3172b;
}

.pi-status__stripped {
  font-weight: 600;
  color: #7a4f00;
}

.pi-status__error {
  max-width: 280px;
  font-size: 11px;
  color: #a3172b;
  word-break: break-word;
}

.pi-status__search .form-control {
  width: 300px;
  max-width: 100%;
}

.pi-page code {
  padding: 0;
  background: none;
  color: #1f2933;
  font-size: 12px;
}

@media (max-width: 767px) {
  .pi-status__search,
  .pi-status__search .form-control {
    width: 100%;
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piStatus.vue